import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { CategoryCard } from "@/components/CategoryCard";
import { JobCard, Job } from "@/components/JobCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Monitor, 
  ShoppingBag, 
  FileText, 
  Heart, 
  GraduationCap, 
  Truck,
  ArrowRight,
  Users,
  Building2,
  Briefcase,
  CheckCircle2
} from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSiteStats } from "@/hooks/useSiteStats";

const categoryIcons = {
  ti: Monitor,
  tecnologia: Monitor,
  vendas: ShoppingBag,
  administrativo: FileText,
  saude: Heart,
  educacao: GraduationCap,
  logistica: Truck,
};

const categoryColors: Record<string, "tech" | "sales" | "admin" | "health" | "education" | "logistics"> = {
  ti: "tech",
  tecnologia: "tech",
  vendas: "sales",
  administrativo: "admin",
  saude: "health",
  educacao: "education",
  logistica: "logistics",
};

const categoriesList = [
  { slug: "ti", title: "Tecnologia" },
  { slug: "vendas", title: "Vendas" },
  { slug: "administrativo", title: "Administrativo" },
  { slug: "saude", title: "Saúde" },
  { slug: "educacao", title: "Educação" },
  { slug: "logistica", title: "Logística" },
];

const Index = () => {
  const [featuredJobs, setFeaturedJobs] = useState<Job[]>([]);
  const [recentJobs, setRecentJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState<Record<string, number>>({});
  const isMobile = useIsMobile();
  const { stats } = useSiteStats();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: jobsData } = await supabase
          .from('jobs')
          .select(`
            *,
            company_profiles (
              id,
              company_name,
              city,
              state,
              plan
            )
          `)
          .eq('is_active', true)
          .eq('status', 'ativa')
          .order('created_at', { ascending: false });

        // Transform to Job format
        const transformJob = (job: any, isFeatured: boolean = false): Job => ({
          id: job.id,
          title: job.title,
          company: job.is_confidential ? 'Empresa Confidencial' : (job.company_profiles?.company_name || 'Empresa'),
          location: job.city && job.state ? `${job.city}, ${job.state}` : 'Brasil',
          salary: job.salary_range_start && job.salary_range_end 
            ? `R$ ${job.salary_range_start.toLocaleString('pt-BR')} - R$ ${job.salary_range_end.toLocaleString('pt-BR')}`
            : job.salary_range_start 
              ? `A partir de R$ ${job.salary_range_start.toLocaleString('pt-BR')}`
              : undefined,
          type: job.contract_type?.[0] || 'CLT',
          modality: job.job_type?.[0] || 'Presencial',
          category: job.area || 'Geral',
          postedAt: new Date(job.created_at).toLocaleDateString('pt-BR'),
          description: job.description?.substring(0, 150) + '...' || '',
          isFeatured,
          isNew: new Date(job.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
        });

        if (jobsData) {
          // Filter featured jobs based on is_featured flag (Professional Plan only)
          const featuredData = jobsData
            .filter(job => job.is_featured === true)
            .slice(0, 6);
          setFeaturedJobs(featuredData.map(job => transformJob(job, true)));

          // Recent jobs (first 4)
          setRecentJobs(jobsData.slice(0, 4).map(job => transformJob(job, false)));

          // Count jobs by area/category
          const areaCounts = jobsData.reduce((acc, job) => {
            const area = job.area || 'outros';
            acc[area] = (acc[area] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          setCategoryStats(areaCounts);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
          <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          
          <div className="container relative">
            <div className="max-w-3xl mx-auto text-center mb-10">
              {stats.jobs > 0 && (
                <Badge variant="featured" className="mb-4">
                  +{stats.jobs} vagas disponíveis
                </Badge>
              )}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 animate-fade-up">
                Encontre sua próxima{" "}
                <span className="text-gradient">oportunidade</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-8 animate-fade-up" style={{ animationDelay: "0.1s" }}>
                Conectamos talentos às melhores empresas. Busque vagas, cadastre seu currículo e conquiste o emprego dos seus sonhos.
              </p>
            </div>
            
            <div className="animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <SearchBar />
            </div>

            <div className="flex flex-wrap justify-center gap-4 mt-6 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              <span className="text-sm text-muted-foreground">Populares:</span>
              <Link to="/vagas?q=desenvolvedor" className="text-sm text-primary hover:underline">Desenvolvedor</Link>
              <Link to="/vagas?q=designer" className="text-sm text-primary hover:underline">Designer</Link>
              <Link to="/vagas?q=vendedor" className="text-sm text-primary hover:underline">Vendedor</Link>
              <Link to="/vagas?q=analista" className="text-sm text-primary hover:underline">Analista</Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 border-y border-border bg-card">
          <div className="container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 mx-auto mb-3">
                  <Briefcase className="h-6 w-6 text-primary" />
                </div>
                <p className="text-2xl md:text-3xl font-display font-bold text-foreground">{stats.jobs}</p>
                <p className="text-sm text-muted-foreground">Vagas Ativas</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-accent/10 mx-auto mb-3">
                  <Building2 className="h-6 w-6 text-accent" />
                </div>
                <p className="text-2xl md:text-3xl font-display font-bold text-foreground">{stats.companies}</p>
                <p className="text-sm text-muted-foreground">Empresas</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-success/10 mx-auto mb-3">
                  <Users className="h-6 w-6 text-success" />
                </div>
                <p className="text-2xl md:text-3xl font-display font-bold text-foreground">{stats.candidates}</p>
                <p className="text-sm text-muted-foreground">Candidatos</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-category-health/10 mx-auto mb-3">
                  <CheckCircle2 className="h-6 w-6 text-category-health" />
                </div>
                <p className="text-2xl md:text-3xl font-display font-bold text-foreground">-</p>
                <p className="text-sm text-muted-foreground">Contratações</p>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-16 lg:py-20">
          <div className="container">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-2">
                  Explore por Categoria
                </h2>
                <p className="text-muted-foreground">Encontre vagas na sua área de interesse</p>
              </div>
              <Button variant="ghost" asChild className="hidden md:flex">
                <Link to="/vagas" className="gap-2">
                  Ver todas <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categoriesList.map((category) => {
                const Icon = categoryIcons[category.slug as keyof typeof categoryIcons];
                const color = categoryColors[category.slug];
                return (
                  <CategoryCard
                    key={category.slug}
                    icon={Icon}
                    title={category.title}
                    count={categoryStats[category.slug] || 0}
                    slug={category.slug}
                    color={color}
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Jobs Section - apenas se houver vagas de plano empresarial */}
        {(loading || featuredJobs.length > 0) && (
          <section className="py-16 lg:py-20 bg-muted/50">
            <div className="container">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-2">
                    Vagas em Destaque
                  </h2>
                  <p className="text-muted-foreground">Oportunidades de empresas premium</p>
                </div>
                <Button variant="outline" asChild className="w-fit">
                  <Link to="/vagas" className="gap-2">
                    Ver todas <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 pt-1">
                {loading ? (
                  <>
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton key={i} className="h-48 rounded-xl" />
                    ))}
                  </>
                ) : (
                  featuredJobs.map((job) => (
                    <JobCard key={job.id} job={job} />
                  ))
                )}
              </div>
            </div>
          </section>
        )}

        {/* Recent Jobs Section */}
        <section className="py-16 lg:py-20">
          <div className="container">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-2">
                  Vagas Recentes
                </h2>
                <p className="text-muted-foreground">As últimas oportunidades cadastradas</p>
              </div>
              <Button variant="ghost" asChild className="w-fit">
                <Link to="/vagas" className="gap-2">
                  Ver todas <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid gap-4 md:gap-3 grid-cols-1 md:grid-cols-2 pt-1">
              {loading ? (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className={isMobile ? "h-48 rounded-xl" : "h-20 rounded-lg"} />
                  ))}
                </>
              ) : recentJobs.length > 0 ? (
                recentJobs.map((job) => (
                  <JobCard key={job.id} job={job} variant={isMobile ? "default" : "compact"} />
                ))
              ) : (
                <p className="text-muted-foreground col-span-2 text-center py-8">
                  Nenhuma vaga disponível no momento
                </p>
              )}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-display font-bold text-primary-foreground mb-4">
                É empresa? Publique suas vagas gratuitamente!
              </h2>
              <p className="text-primary-foreground/80 mb-8">
                Cadastre sua empresa e comece a receber currículos de candidatos qualificados.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="hero-secondary" size="lg" asChild>
                  <Link to="/entrar">Cadastrar Empresa</Link>
                </Button>
                <Button variant="ghost" size="lg" className="text-primary-foreground hover:bg-primary-foreground/10" asChild>
                  <Link to="/planos">Conhecer Planos</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
